import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../api.js';
import { useLang } from '../i18n.js';
import { thb, thbShort, periodNow, shiftPeriod, relTime, shortDate } from '../money.js';
import { Card, Empty, Spinner, Avatar, Chip, Overlay, PeriodStepper } from '../ui.jsx';

/**
 * Admin.
 *
 * Every tab here changes what the whole team sees, so each one shows who
 * changed a thing last and every write is audited server-side. Tabs load their
 * own data on first open rather than all at once — this is a settings screen,
 * not a dashboard, and nobody needs seven queries to edit one stage list.
 */
const TABS = [
  ['users', 'tabUsers'],
  ['rules', 'tabRules'],
  ['quotas', 'tabQuotas'],
  ['targets', 'tabTargets'],
  ['connections', 'tabConnections'],
  ['exports', 'tabExports'],
  ['jobs', 'tabJobs'],
  ['audit', 'tabAudit'],
];

export default function Admin({ me, toast }) {
  const { t } = useLang();
  const [tab, setTab] = useState('users');
  const [rebuilding, setRebuilding] = useState(false);

  // The nav hides this for non-admins, but a hash is a URL and someone will
  // paste one. Say so plainly instead of rendering seven tabs of 403s.
  if (me.role !== 'admin') {
    return (
      <div className="page">
        <div className="display" style={{ fontSize: 22 }}>
          {t('navAdmin')}
        </div>
        <p className="dim" style={{ marginTop: 8, fontSize: 13.5 }}>
          {t('adminOnly')}
        </p>
      </div>
    );
  }

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const r = await api.rebuildRollups(periodNow());
      toast(t('figuresRebuilt', { ms: r.ms }));
    } catch (e) {
      toast(e.message);
    } finally {
      setRebuilding(false);
    }
  };

  return (
    <div className="page">
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
          marginBottom: 16,
        }}
      >
        <div>
          <div className="display" style={{ fontSize: 26 }}>
            {t('navAdmin')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 3 }}>{t('adminBlurb')}</div>
        </div>
        <button className="btn" disabled={rebuilding} onClick={rebuild} title={t('rebuildHint')}>
          {rebuilding ? t('rebuilding') : t('rebuildFigures')}
        </button>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
        {TABS.map(([key, label]) => (
          <Chip key={key} on={tab === key} onClick={() => setTab(key)}>
            {t(label)}
          </Chip>
        ))}
      </div>

      {tab === 'users' && <UsersTab me={me} toast={toast} />}
      {tab === 'rules' && <RulesTab toast={toast} />}
      {tab === 'quotas' && <QuotasTab toast={toast} />}
      {tab === 'targets' && <TargetsTab toast={toast} />}
      {tab === 'connections' && <ConnectionsTab toast={toast} />}
      {tab === 'exports' && <ExportsTab />}
      {tab === 'jobs' && <JobsTab toast={toast} />}
      {tab === 'audit' && <AuditTab />}
    </div>
  );
}

/** Shared loader: every tab is "fetch once, show a spinner, show the error". */
function useTabData(fetcher, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    setData(null);
    setError('');
    fetcher()
      .then(setData)
      .catch((e) => setError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => {
    load();
  }, [load]);
  return { data, error, load };
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

function UsersTab({ me, toast }) {
  const { t, lang } = useLang();
  const { data, error, load } = useTabData(() => api.adminUsers());
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState('');

  const patch = async (id, body) => {
    setBusy(id);
    try {
      await api.updateUser(id, body);
      toast(t('saved'));
      load();
    } catch (e) {
      toast(e.message);
      load(); // the select has already moved; put it back to the truth
    } finally {
      setBusy('');
    }
  };

  const remove = async (id) => {
    setBusy(id);
    try {
      await api.deleteUser(id);
      toast(t('userDeleted'));
      setConfirming(null);
      load();
    } catch (e) {
      toast(e.message);
      setConfirming(null);
    } finally {
      setBusy('');
    }
  };

  if (error) return <div className="page">{error}</div>;
  if (!data) return <Spinner label={t('loading')} />;

  return (
    <>
      <div style={{ display: 'flex', marginBottom: 12 }}>
        <button
          className="btn btn-primary btn-sm"
          style={{ marginLeft: 'auto' }}
          onClick={() => setCreating(true)}
        >
          {t('newUser')}
        </button>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>{t('person')}</th>
              <th>{t('role')}</th>
              <th>{t('entities')}</th>
              <th>{t('reportsTo')}</th>
              <th style={{ textAlign: 'right' }}>{t('records')}</th>
              <th>{t('lastLogin')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => {
              const owned = Number(u.deals) + Number(u.companies);
              return (
                <tr key={u.id} style={u.active ? undefined : { opacity: 0.55 }}>
                  <td style={{ whiteSpace: 'normal', minWidth: 190 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Avatar initials={u.avatar_initials} seed={u.avatar_var} size={24} />
                      <span>
                        <span style={{ display: 'block', fontWeight: 600 }}>
                          {u.name}
                          {u.id === me.id && (
                            <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>
                              {' '}
                              ({t('you')})
                            </span>
                          )}
                        </span>
                        <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{u.email}</span>
                      </span>
                    </span>
                  </td>
                  <td>
                    <select
                      className="select"
                      style={{ height: 28, fontSize: 12 }}
                      value={u.role}
                      disabled={busy === u.id}
                      onChange={(e) => patch(u.id, { role: e.target.value })}
                    >
                      <option value="sales">sales</option>
                      <option value="ae">ae</option>
                      <option value="admin">admin</option>
                    </select>
                  </td>
                  <td>
                    <select
                      className="select"
                      style={{ height: 28, fontSize: 12 }}
                      value={u.entities.join(',')}
                      disabled={busy === u.id}
                      onChange={(e) => patch(u.id, { entities: e.target.value.split(',') })}
                    >
                      <option value="BTM">BTM</option>
                      <option value="MR">MR</option>
                      <option value="BTM,MR">BTM + MR</option>
                    </select>
                  </td>
                  <td style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                    {u.reports_to_name || '—'}
                  </td>
                  <td className="num" style={{ textAlign: 'right', fontSize: 12.5 }}>
                    {owned || '—'}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                    {u.last_login_at ? relTime(u.last_login_at, lang) : t('never')}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      className="btn btn-sm"
                      disabled={busy === u.id}
                      onClick={() => patch(u.id, { active: !u.active })}
                    >
                      {u.active ? t('deactivate') : t('reactivate')}
                    </button>
                    {/* Hard delete only for someone with nothing against their
                        name; the API refuses otherwise, so don't offer it. */}
                    {owned === 0 && u.id !== me.id && (
                      <button
                        className="btn btn-sm"
                        style={{
                          marginLeft: 6,
                          color: confirming === u.id ? 'var(--danger)' : undefined,
                          fontWeight: confirming === u.id ? 700 : undefined,
                        }}
                        disabled={busy === u.id}
                        onClick={() =>
                          confirming === u.id ? remove(u.id) : setConfirming(u.id)
                        }
                        onBlur={() => setConfirming(null)}
                      >
                        {confirming === u.id ? t('confirmDelete') : t('delete')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && (
        <NewUserModal
          users={data.users.filter((u) => u.active)}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            toast(t('userCreated'));
            load();
          }}
        />
      )}
    </>
  );
}

function NewUserModal({ users, onClose, onDone }) {
  const { t } = useLang();
  const [f, setF] = useState({
    name: '',
    name_th: '',
    email: '',
    mobile: '',
    role: 'sales',
    entities: ['BTM'],
    reports_to: '',
    start_date: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const toggleEntity = (e) =>
    setF((s) => ({
      ...s,
      entities: s.entities.includes(e)
        ? s.entities.filter((x) => x !== e)
        : [...s.entities, e],
    }));

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.createUser({
        name: f.name,
        name_th: f.name_th || null,
        email: f.email,
        mobile: f.mobile || null,
        role: f.role,
        entities: f.entities,
        reports_to: f.reports_to || null,
        start_date: f.start_date || null,
      });
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClose={onClose} kind="modal">
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '18px 20px 10px' }}>
          <div className="display" style={{ fontSize: 18 }}>
            {t('newUser')}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 2 }}>
            {t('newUserBlurb')}
          </div>
        </div>

        <div style={{ padding: '0 20px', overflow: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label className="field-label">{t('fullName')}</label>
              <input
                className="input"
                required
                autoFocus
                value={f.name}
                onChange={(e) => set('name', e.target.value)}
              />
            </div>
            <div>
              <label className="field-label">{t('thaiName')}</label>
              <input
                className="input"
                value={f.name_th}
                onChange={(e) => set('name_th', e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
            <div>
              <label className="field-label">{t('workEmail')}</label>
              <input
                className="input"
                type="email"
                required
                value={f.email}
                onChange={(e) => set('email', e.target.value)}
              />
            </div>
            <div>
              <label className="field-label">{t('mobile')}</label>
              <input
                className="input"
                value={f.mobile}
                onChange={(e) => set('mobile', e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
            <div>
              <label className="field-label">{t('role')}</label>
              <select
                className="select"
                value={f.role}
                onChange={(e) => set('role', e.target.value)}
              >
                <option value="sales">sales</option>
                <option value="ae">ae</option>
                <option value="admin">admin</option>
              </select>
            </div>
            <div>
              <label className="field-label">{t('reportsTo')}</label>
              <select
                className="select"
                value={f.reports_to}
                onChange={(e) => set('reports_to', e.target.value)}
              >
                <option value="">—</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <label className="field-label" style={{ marginTop: 14 }}>
            {t('entities')}
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            {['BTM', 'MR'].map((e) => (
              <Chip key={e} on={f.entities.includes(e)} onClick={() => toggleEntity(e)}>
                {e}
              </Chip>
            ))}
          </div>

          <label className="field-label" style={{ marginTop: 14 }}>
            {t('startDate')}
          </label>
          <input
            className="input"
            type="date"
            value={f.start_date}
            onChange={(e) => set('start_date', e.target.value)}
          />

          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 12, lineHeight: 1.6 }}>
            {t('newUserNote')}
          </div>

          {error && (
            <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 12 }}>{error}</div>
          )}
        </div>

        <div className="drawer-foot" style={{ marginTop: 14 }}>
          <button type="button" className="btn" onClick={onClose} style={{ marginLeft: 'auto' }}>
            {t('cancel')}
          </button>
          <button
            className="btn btn-primary"
            disabled={busy || !f.name || !f.email || f.entities.length === 0}
          >
            {t('createUser')}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

// ---------------------------------------------------------------------------
// Pipeline rules (config)
// ---------------------------------------------------------------------------

function RulesTab({ toast }) {
  const { t } = useLang();
  const { data, error, load } = useTabData(() => api.adminConfig());

  if (error) return <div className="page">{error}</div>;
  if (!data) return <Spinner label={t('loading')} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{t('rulesBlurb')}</div>
      {Object.entries(data.groups).map(([group, rows]) => (
        <Card key={group} title={group}>
          {rows.map((row) => (
            <ConfigRow key={row.key} row={row} onSaved={load} toast={toast} />
          ))}
        </Card>
      ))}
    </div>
  );
}

/** A csv or json value needs room to read; a number does not. */
const isLong = (row) => row.type === 'json' || row.type === 'csv' || row.value.length > 48;

function ConfigRow({ row, onSaved, toast }) {
  const { t, lang } = useLang();
  const [value, setValue] = useState(row.value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dirty = value !== row.value;
  // `<input type="number">` hands back '' for anything it cannot parse, so the
  // field can look full ("--e") while the value is empty. Saving that writes a
  // blank the server would have to reject; say so here instead.
  const blankNumber = row.type === 'number' && value.trim() === '';

  useEffect(() => {
    setValue(row.value);
  }, [row.value]);

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await api.updateConfig(row.key, value);
      toast(t('saved'));
      onSaved();
    } catch (e) {
      // Removing a stage that still has deals in it comes back here as a 409
      // with the count in it — worth showing next to the field, not as a toast
      // that disappears before it has been read.
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ padding: '10px 2px', borderBottom: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{row.label || row.key}</span>
        <span className="num" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          {row.key}
        </span>
        {row.updated_by_name && (
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
            {t('editedBy', { name: row.updated_by_name })} · {relTime(row.updated_at, lang)}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        {isLong(row) ? (
          <textarea
            className="textarea"
            rows={row.type === 'json' ? 4 : 2}
            style={row.type === 'json' ? { fontFamily: 'var(--f-mono)', fontSize: 12 } : undefined}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        ) : (
          <input
            className="input"
            type={row.type === 'number' ? 'number' : 'text'}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
        <button
          className="btn btn-sm btn-primary"
          style={{ visibility: dirty ? 'visible' : 'hidden', flexShrink: 0 }}
          disabled={busy || blankNumber}
          onClick={save}
        >
          {t('save')}
        </button>
      </div>

      {(error || (dirty && blankNumber)) && (
        <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6 }}>
          {error || t('needsANumber')}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quotas
// ---------------------------------------------------------------------------

function QuotasTab({ toast }) {
  const { t } = useLang();
  // Quotas are set ahead of the month, so unlike Review this picker goes
  // forward — just not indefinitely.
  const [period, setPeriod] = useState(periodNow);
  const { data, error, load } = useTabData(() => api.quotas(period), [period]);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEdits({});
  }, [period, data]);

  const baht = (satang) => String((Number(satang) || 0) / 100);
  const valueOf = (row, key) => edits[row.user_id]?.[key] ?? baht(row[key]);
  const setValue = (userId, key, v) =>
    setEdits((s) => ({ ...s, [userId]: { ...s[userId], [key]: v } }));

  const dirty = Object.keys(edits).length > 0;

  const save = async () => {
    setBusy(true);
    try {
      const rows = data.quotas.map((r) => ({
        user_id: r.user_id,
        quota_new_satang: Math.round(Number(valueOf(r, 'quota_new_satang') || 0) * 100),
        quota_upsell_satang: Math.round(Number(valueOf(r, 'quota_upsell_satang') || 0) * 100),
      }));
      await api.saveQuotas(period, rows);
      toast(t('quotasSaved'));
      load();
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <PeriodStepper period={period} onChange={setPeriod} max={shiftPeriod(periodNow(), 12)} />
        <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{t('quotasBlurb')}</span>
        <button
          className="btn btn-primary btn-sm"
          style={{ marginLeft: 'auto' }}
          disabled={busy || !dirty || !data}
          onClick={save}
        >
          {t('saveQuotas')}
        </button>
      </div>

      {error && <div className="page">{error}</div>}
      {!error && !data && <Spinner label={t('loading')} />}

      {data && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('person')}</th>
                <th>{t('entities')}</th>
                <th style={{ textAlign: 'right' }}>{t('quotaNew')}</th>
                <th style={{ textAlign: 'right' }}>{t('quotaUpsell')}</th>
                <th style={{ textAlign: 'right' }}>{t('total')}</th>
              </tr>
            </thead>
            <tbody>
              {data.quotas.map((r) => {
                const total =
                  Number(valueOf(r, 'quota_new_satang') || 0) +
                  Number(valueOf(r, 'quota_upsell_satang') || 0);
                return (
                  <tr key={r.user_id}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <Avatar initials={r.avatar_initials} seed={r.avatar_var} size={22} />
                        <span>
                          <span style={{ display: 'block', fontWeight: 600 }}>{r.name}</span>
                          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{r.role}</span>
                        </span>
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      {r.entities.join(' + ') || '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        className="input num"
                        type="number"
                        min="0"
                        step="1000"
                        style={{ width: 130, height: 30, textAlign: 'right' }}
                        value={valueOf(r, 'quota_new_satang')}
                        onChange={(e) => setValue(r.user_id, 'quota_new_satang', e.target.value)}
                      />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <input
                        className="input num"
                        type="number"
                        min="0"
                        step="1000"
                        style={{ width: 130, height: 30, textAlign: 'right' }}
                        value={valueOf(r, 'quota_upsell_satang')}
                        onChange={(e) =>
                          setValue(r.user_id, 'quota_upsell_satang', e.target.value)
                        }
                      />
                    </td>
                    <td className="num" style={{ textAlign: 'right', fontWeight: 700 }}>
                      {thbShort(Math.round(total * 100))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Annual targets
// ---------------------------------------------------------------------------

function TargetsTab({ toast }) {
  const { t } = useLang();
  const [year, setYear] = useState(() => Number(periodNow().slice(0, 4)));
  const { data, error, load } = useTabData(() => api.targets(year), [year]);

  if (error) return <div className="page">{error}</div>;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button className="icon-btn" onClick={() => setYear((y) => y - 1)}>
            ‹
          </button>
          <span className="display num" style={{ fontSize: 15, minWidth: 56, textAlign: 'center' }}>
            {year}
          </span>
          <button className="icon-btn" onClick={() => setYear((y) => y + 1)}>
            ›
          </button>
        </div>
        <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{t('targetsBlurb')}</span>
      </div>

      {!data ? (
        <Spinner label={t('loading')} />
      ) : (
        <div className="grid-auto">
          {data.targets.map((row) => (
            <TargetCard key={row.entity} row={row} year={year} onSaved={load} toast={toast} />
          ))}
        </div>
      )}
    </>
  );
}

function TargetCard({ row, year, onSaved, toast }) {
  const { t } = useLang();
  const [value, setValue] = useState(String(row.amount_satang / 100));
  const [busy, setBusy] = useState(false);
  const dirty = Math.round(Number(value || 0) * 100) !== row.amount_satang;

  useEffect(() => {
    setValue(String(row.amount_satang / 100));
  }, [row.amount_satang, year]);

  const save = async () => {
    setBusy(true);
    try {
      await api.saveTarget({
        entity: row.entity,
        year,
        amount_satang: Math.round(Number(value || 0) * 100),
      });
      toast(t('targetSaved'));
      onSaved();
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy(false);
    }
  };

  const pct = row.attainment_pct;

  return (
    <Card
      title={`${row.entity} · ${year}`}
      action={
        dirty && (
          <button className="btn btn-sm btn-primary" disabled={busy} onClick={save}>
            {t('save')}
          </button>
        )
      }
    >
      <div style={{ padding: '4px 2px 2px' }}>
        <label className="field-label">{t('annualTarget')}</label>
        <input
          className="input num"
          type="number"
          min="0"
          step="100000"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />

        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 8,
            margin: '14px 0 6px',
            flexWrap: 'wrap',
          }}
        >
          <span className="display num" style={{ fontSize: 22 }}>
            {thb(row.won_satang)}
          </span>
          <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{t('wonThisYear')}</span>
          <span
            className="num"
            style={{
              marginLeft: 'auto',
              fontWeight: 700,
              color:
                pct === null ? 'var(--ink-3)' : pct >= 100 ? 'var(--success)' : 'var(--ink-2)',
            }}
          >
            {pct === null ? t('noTargetSet') : `${pct}%`}
          </span>
        </div>

        <div
          style={{ height: 10, borderRadius: 5, background: 'var(--sunken)', overflow: 'hidden' }}
        >
          <span
            style={{
              display: 'block',
              height: '100%',
              width: `${Math.min(pct ?? 0, 100)}%`,
              background: row.entity === 'BTM' ? 'var(--entity-btm)' : 'var(--entity-mr)',
            }}
          />
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Connections
// ---------------------------------------------------------------------------

const CONNECTION_STATUS = {
  connected: ['var(--success)', 'var(--success-soft)'],
  degraded: ['var(--warning)', 'var(--warning-soft)'],
  error: ['var(--danger)', 'var(--danger-soft)'],
  disconnected: ['var(--ink-3)', 'var(--sunken)'],
};

function ConnectionsTab({ toast }) {
  const { t, lang } = useLang();
  const { data, error, load } = useTabData(() => api.connections());
  const [busy, setBusy] = useState('');

  const setStatus = async (provider, status) => {
    setBusy(provider);
    try {
      await api.updateConnection(provider, { status });
      toast(t('saved'));
      load();
    } catch (e) {
      toast(e.message);
      load();
    } finally {
      setBusy('');
    }
  };

  if (error) return <div className="page">{error}</div>;
  if (!data) return <Spinner label={t('loading')} />;

  return (
    <>
      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 12 }}>
        {t('connectionsBlurb')}
      </div>
      <div className="grid-auto">
        {data.connections.map((conn) => {
          const [fg, bg] = CONNECTION_STATUS[conn.status] || CONNECTION_STATUS.disconnected;
          const configured = data.configured[conn.provider];
          return (
            <Card key={conn.provider} title={conn.provider}>
              <div style={{ padding: '2px 2px 6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span className="pill" style={{ background: bg, color: fg }}>
                    {conn.status}
                  </span>
                  {/* "not configured" and "configured but failing" are different
                      problems and the first thing you need to tell apart. */}
                  <span style={{ fontSize: 11.5, color: configured ? 'var(--success)' : 'var(--ink-3)' }}>
                    {configured ? t('credsPresent') : t('credsMissing')}
                  </span>
                </div>

                <div style={{ fontSize: 12.5, color: 'var(--ink-2)', minHeight: 18 }}>
                  {conn.detail || '—'}
                </div>

                <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 8 }}>
                  {t('lastSync')}:{' '}
                  {conn.last_sync_at ? relTime(conn.last_sync_at, lang) : t('never')}
                </div>
                {conn.last_error && (
                  <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 4 }}>
                    {conn.last_error}
                  </div>
                )}

                <select
                  className="select"
                  style={{ marginTop: 10, height: 30, fontSize: 12 }}
                  value={conn.status}
                  disabled={busy === conn.provider}
                  onChange={(e) => setStatus(conn.provider, e.target.value)}
                >
                  {Object.keys(CONNECTION_STATUS).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </Card>
          );
        })}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

function ExportsTab() {
  const { t } = useLang();
  const { data, error } = useTabData(() => api.exports());

  if (error) return <div className="page">{error}</div>;
  if (!data) return <Spinner label={t('loading')} />;

  return (
    <>
      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 12 }}>
        {t('exportsBlurb')}
      </div>
      <div className="grid-auto">
        {data.exports.map((x) => (
          <Card key={x.name}>
            <div style={{ padding: '2px 2px 4px' }}>
              <div style={{ fontWeight: 700, fontSize: 14, textTransform: 'capitalize' }}>
                {x.name}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '3px 0 12px' }}>
                {t('nColumns', { n: x.columns })}
              </div>
              {/* A real link, not a fetch: the response is an attachment and the
                  browser's own download handling is better than ours. */}
              <a className="btn btn-sm" href={x.href} download>
                {t('downloadCsv')}
              </a>
            </div>
          </Card>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

/**
 * Scheduled work, and whether it is actually happening.
 *
 * The cron Worker runs at 02:00 with nobody watching, so the only thing that
 * separates "working" from "silently broken for three weeks" is this history.
 * Each job shows its last outcome and the detail line it returned — the detail
 * is the useful part: "would have sent 11" and "sent 11" both read as success
 * everywhere else.
 */
function JobsTab({ toast }) {
  const { t, lang } = useLang();
  const { data, error, load } = useTabData(() => api.jobs());
  const [busy, setBusy] = useState('');

  const run = async (name) => {
    setBusy(name);
    try {
      const r = await api.runJobNow(name);
      // The endpoint answers 200 for a job that failed — the request worked and
      // the verdict is in the body. Reporting on res.ok alone would call every
      // failure a success.
      toast(r.ok ? t('jobOk', { ms: r.ms }) : t('jobFailed', { detail: r.detail }));
      load();
    } catch (e) {
      toast(e.message);
    } finally {
      setBusy('');
    }
  };

  if (error) return <div className="page">{error}</div>;
  if (!data) return <Spinner label={t('loading')} />;

  const verdict = (r) => {
    if (!r) return ['var(--ink-3)', 'var(--sunken)', t('neverRun')];
    if (r.ok === null || r.ok === undefined)
      // Started, never finished — the isolate died mid-job.
      return ['var(--warning)', 'var(--warning-soft)', t('jobIncomplete')];
    return r.ok
      ? ['var(--success)', 'var(--success-soft)', t('jobSucceeded')]
      : ['var(--danger)', 'var(--danger-soft)', t('jobFailedShort')];
  };

  return (
    <>
      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 12 }}>
        {t('jobsBlurb')}
      </div>

      {!data.cron_configured && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            padding: '10px 12px',
            marginBottom: 14,
            borderRadius: 'var(--r-md)',
            background: 'var(--warning-soft)',
            color: 'var(--warning)',
            fontSize: 12.5,
            lineHeight: 1.55,
          }}
        >
          <span style={{ fontWeight: 700 }}>!</span>
          {/* Without this an empty history is ambiguous: never deployed, or
              deployed and failing? They need different fixes. */}
          <span>{t('cronNotConfigured')}</span>
        </div>
      )}

      <div className="grid-auto" style={{ marginBottom: 20 }}>
        {data.jobs.map(({ name, last }) => {
          const [fg, bg, label] = verdict(last);
          return (
            <Card key={name}>
              <div style={{ padding: '2px 2px 4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="num" style={{ fontWeight: 700, fontSize: 13.5 }}>
                    {name}
                  </span>
                  <span className="pill" style={{ background: bg, color: fg }}>
                    {label}
                  </span>
                </div>

                <div style={{ fontSize: 11.5, color: 'var(--ink-3)', margin: '7px 0 2px' }}>
                  {last
                    ? `${relTime(last.started_at, lang)}${last.ms != null ? ` · ${last.ms}ms` : ''}${
                        last.trigger === 'manual' ? ` · ${t('manualRun')}` : ''
                      }`
                    : t('neverRun')}
                </div>
                {last?.detail && (
                  <div
                    style={{
                      fontSize: 12,
                      color: last.ok ? 'var(--ink-2)' : 'var(--danger)',
                      lineHeight: 1.5,
                      wordBreak: 'break-word',
                    }}
                  >
                    {last.detail}
                  </div>
                )}

                <button
                  className="btn btn-sm"
                  style={{ marginTop: 10 }}
                  disabled={busy === name}
                  onClick={() => run(name)}
                >
                  {busy === name ? t('running') : t('runNow')}
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      {data.runs.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t('when')}</th>
                <th>{t('job')}</th>
                <th>{t('result')}</th>
                <th style={{ textAlign: 'right' }}>{t('duration')}</th>
                <th>{t('detail')}</th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((r) => (
                <tr key={r.id}>
                  <td style={{ fontSize: 12, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                    {relTime(r.started_at, lang)}
                  </td>
                  <td className="num" style={{ fontSize: 12 }}>
                    {r.name}
                    {r.trigger === 'manual' && (
                      <span style={{ color: 'var(--ink-3)' }}> · {t('manualRun')}</span>
                    )}
                  </td>
                  <td
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color:
                        r.ok === null || r.ok === undefined
                          ? 'var(--warning)'
                          : r.ok
                            ? 'var(--success)'
                            : 'var(--danger)',
                    }}
                  >
                    {verdict(r)[2]}
                  </td>
                  <td className="num" style={{ textAlign: 'right', fontSize: 12 }}>
                    {r.ms != null ? `${r.ms}ms` : '—'}
                  </td>
                  <td
                    style={{
                      fontSize: 12,
                      color: 'var(--ink-2)',
                      whiteSpace: 'normal',
                      maxWidth: 420,
                    }}
                  >
                    {r.detail || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

function AuditTab() {
  const { t, lang } = useLang();
  const { data, error } = useTabData(() => api.auditLog(150));

  if (error) return <div className="page">{error}</div>;
  if (!data) return <Spinner label={t('loading')} />;
  if (!data.entries.length) return <Empty>{t('noAuditYet')}</Empty>;

  return (
    <>
      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 12 }}>
        {t('auditBlurb')}
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>{t('when')}</th>
              <th>{t('who')}</th>
              <th>{t('action')}</th>
              <th>{t('target')}</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((e) => (
              <tr key={e.id}>
                <td style={{ fontSize: 12, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                  {shortDate(e.created_at, lang)} · {relTime(e.created_at, lang)}
                </td>
                <td style={{ fontSize: 12.5 }}>{e.user_name || t('system')}</td>
                <td className="num" style={{ fontSize: 12 }}>
                  {e.action}
                </td>
                <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                  {e.target_type}
                  {e.target_id ? ` · ${e.target_id}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
